import type { IconDefinition } from '../../shared/iconDefinition';
import type { ReactionBucketRow } from '../../shared/reactionBucketRow';
import { familyColorScale } from '../familyColorScale';
import { formatVideoClock } from '../formatVideoClock';
import { iconColorScale } from '../iconColorScale';
import { chartGeometry } from './chartGeometry';

// Display order, coarse to fine, so the stack reads consistently across buckets.
const FAMILY_ORDER = ['delighted', 'struck', 'provoked', 'lost'];

// Stacked bands of reaction counts per time bucket. Hand-rolled SVG rects — the
// other half of "no charting library" (spec §2).
//
// Two resolutions of the same data (DESIGN-DECISIONS §4, "collect wide, display
// rolled up"): family view is the default because at N~10-15 per-icon counts are
// mostly noise, and icon view unlocks the detail once a moment is worth looking
// at closely. Fidelity is never lost on the way in — the toggle only changes what
// is summed on the way out.
export function ReactionTimelineChart(input: {
  buckets: ReactionBucketRow[];
  icons: IconDefinition[];
  durationMs: number;
  bucketMs: number;
  mode: 'family' | 'icon';
  width: number;
  height: number;
}) {
  const geometry = chartGeometry({
    width: input.width,
    height: input.height,
    durationMs: input.durationMs
  });

  // Acceptance criterion 7 again: an empty chart must say something, not crash
  // and not silently render a blank frame that looks like a bug.
  if (input.buckets.length === 0) {
    return (
      <div className="chartEmpty" style={{ height: input.height }}>
        No reactions recorded yet.
      </div>
    );
  }

  const seriesKeys = input.mode === 'family'
    ? FAMILY_ORDER.filter((family) => input.icons.some((icon) => icon.family === family))
    : input.icons.map((icon) => icon.id);

  const colors = input.mode === 'family' ? familyColorScale : iconColorScale(input.icons);
  const labelFor = (key: string) =>
    input.mode === 'family' ? key : (input.icons.find((icon) => icon.id === key)?.label ?? key);

  // Scale to the busiest bucket rather than a fixed ceiling. Absolute counts are
  // meaningless at this sample size; what the creator is reading is the shape.
  const peak = Math.max(1, ...input.buckets.map((bucket) => bucket.total));
  const barWidth = geometry.widthForSpan(input.bucketMs);

  return (
    <>
      <svg
        className="chartSvg"
        viewBox={`0 0 ${input.width} ${input.height}`}
        role="img"
        aria-label="Reactions over video time, stacked by emotion family"
      >
        <line
          x1={geometry.padLeft}
          x2={input.width - geometry.padRight}
          y1={geometry.baselineY}
          y2={geometry.baselineY}
          className="chartGridline"
        />
        <text x={4} y={geometry.yForFraction(1) + 4} className="chartAxisLabel">
          {peak}
        </text>

        {input.buckets.map((bucket) => {
          let stackedBelow = 0;
          return (
            <g key={bucket.t_ms}>
              {seriesKeys.map((key) => {
                const counts = input.mode === 'family' ? bucket.by_family : bucket.by_icon;
                const value = counts[key] ?? 0;
                if (value === 0) return null;
                const yTop = geometry.yForFraction((stackedBelow + value) / peak);
                const yBottom = geometry.yForFraction(stackedBelow / peak);
                stackedBelow += value;
                return (
                  <rect
                    key={key}
                    x={geometry.xForTime(bucket.t_ms)}
                    y={yTop}
                    width={Math.max(1, barWidth - 1)}
                    height={Math.max(1, yBottom - yTop)}
                    fill={colors[key] ?? '#7c869c'}
                  >
                    <title>{`${formatVideoClock(bucket.t_ms)} — ${labelFor(key)}: ${value}`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}

        <text x={geometry.padLeft} y={input.height - 6} className="chartAxisLabel">
          0:00
        </text>
        <text x={input.width - geometry.padRight} y={input.height - 6} className="chartAxisLabel" textAnchor="end">
          {formatVideoClock(input.durationMs)}
        </text>
      </svg>

      <div className="chartLegend">
        {seriesKeys.map((key) => (
          <span className="chartLegendItem" key={key}>
            <i style={{ background: colors[key] ?? '#7c869c' }} />
            {labelFor(key)}
          </span>
        ))}
      </div>
    </>
  );
}
